import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useWhatsApps from "../../hooks/useWhatsApps";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  dialogTitle: {
    position: "relative",
    paddingRight: theme.spacing(7),
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: "50%",
    transform: "translateY(-50%)",
    color: theme.palette.text.secondary,
  },
  footerCloseButton: {
    minWidth: 96,
    borderColor: theme.palette.text.secondary,
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.03)",
    "&:hover": {
      borderColor: theme.palette.text.primary,
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.1)"
          : "rgba(0, 0, 0, 0.07)",
    },
  },
  controls: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "flex-end",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  connectionField: {
    minWidth: 240,
  },
  searchField: {
    flex: 1,
    minWidth: 220,
  },
  statusFilterField: {
    minWidth: 205,
  },
  sourceFilterField: {
    minWidth: 220,
  },
  filterLabel: {
    whiteSpace: "nowrap",
  },
  tooltipOption: {
    display: "block",
    width: "100%",
  },
  summary: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  summaryChip: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(66, 133, 244, 0.14)"
        : "rgba(25, 118, 210, 0.07)",
    fontWeight: 600,
  },
  tableContainer: {
    maxHeight: 430,
    overflow: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },
  nameSource: {
    display: "block",
    color: theme.palette.text.secondary,
  },
  protectedName: {
    display: "block",
    color: theme.palette.warning?.main || "#f9a825",
  },
  loading: {
    minHeight: 180,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  result: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
}));

const ImportContactsModal = ({ open, onClose, onImported }) => {
  const classes = useStyles();
  const { whatsApps, loading: loadingWhatsApps } = useWhatsApps();
  const [whatsappId, setWhatsappId] = useState("");
  const [preview, setPreview] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const connectedWhatsApps = useMemo(
    () => (whatsApps || []).filter(item => item.status === "CONNECTED"),
    [whatsApps]
  );

  useEffect(() => {
    if (!open) return;

    const preferred =
      connectedWhatsApps.find(item => item.isDefault) || connectedWhatsApps[0];
    setWhatsappId(preferred ? String(preferred.id) : "");
    setPreview(null);
    setSelectedKeys(new Set());
    setSearch("");
    setStatusFilter("all");
    setSourceFilter("all");
    setResult(null);
    setPage(0);
  }, [open, connectedWhatsApps]);

  const loadPreview = async () => {
    if (!whatsappId) return;

    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.get("/contacts/import/preview", {
        params: { whatsappId },
      });
      setPreview(data);
      setSelectedKeys(
        new Set(
          data.contacts
            .filter(contact => contact.selectedByDefault)
            .map(contact => contact.key)
        )
      );
      setPage(0);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = useMemo(() => {
    if (!preview) return [];
    const normalizedSearch = search.trim().toLowerCase();

    return preview.contacts.filter(contact => {
      const matchesStatus =
        statusFilter === "all" || contact.status === statusFilter;
      const matchesSource =
        sourceFilter === "all" ||
        (sourceFilter === "named" && contact.nameSource !== "provisional") ||
        (sourceFilter === "addressBook" &&
          contact.nameSource === "whatsapp_address_book") ||
        (sourceFilter === "verified" &&
          contact.nameSource === "whatsapp_verified") ||
        (sourceFilter === "profile" &&
          contact.nameSource === "whatsapp_profile") ||
        (sourceFilter === "provisional" &&
          contact.nameSource === "provisional");
      const matchesSearch =
        !normalizedSearch ||
        contact.name.toLowerCase().includes(normalizedSearch) ||
        contact.number.includes(normalizedSearch) ||
        (contact.existingName || "").toLowerCase().includes(normalizedSearch);
      return matchesStatus && matchesSource && matchesSearch;
    });
  }, [preview, search, statusFilter, sourceFilter]);

  const pagedContacts = filteredContacts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const toggleContact = key => {
    setSelectedKeys(previous => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allFilteredSelected =
    filteredContacts.length > 0 &&
    filteredContacts.every(contact => selectedKeys.has(contact.key));
  const someFilteredSelected = filteredContacts.some(contact =>
    selectedKeys.has(contact.key)
  );

  const toggleAllFiltered = () => {
    setSelectedKeys(previous => {
      const next = new Set(previous);
      if (allFilteredSelected) {
        filteredContacts.forEach(contact => next.delete(contact.key));
      } else {
        filteredContacts.forEach(contact => next.add(contact.key));
      }
      return next;
    });
  };

  const selectRecommended = () => {
    if (!preview) return;
    setSelectedKeys(
      new Set(
        preview.contacts
          .filter(contact => contact.status === "new" || contact.status === "update")
          .map(contact => contact.key)
      )
    );
  };

  const handleImport = async () => {
    if (!selectedKeys.size) return;

    setImporting(true);
    try {
      const { data } = await api.post("/contacts/import", {
        whatsappId: Number(whatsappId),
        selectedKeys: Array.from(selectedKeys),
      });
      setResult(data);
      toast.success(i18n.t("contactImport.completed"));
      if (onImported) onImported();
    } catch (err) {
      toastError(err);
    } finally {
      setImporting(false);
    }
  };

  const statusChip = status => (
    <Chip
      size="small"
      label={i18n.t(`contactImport.status.${status}`)}
      color={status === "new" || status === "update" ? "primary" : "default"}
      variant={status === "protected" ? "outlined" : "default"}
    />
  );

  const renderSummaryChip = (key, value) => (
    <Tooltip
      key={key}
      title={i18n.t(`contactImport.summaryHelp.${key}`)}
      placement="top"
      enterDelay={300}
    >
      <Chip
        className={classes.summaryChip}
        variant="outlined"
        label={`${i18n.t(`contactImport.summary.${key}`)}: ${value}`}
      />
    </Tooltip>
  );

  return (
    <Dialog open={open} onClose={importing ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        {i18n.t("contactImport.title")}
        <Tooltip title={i18n.t("contactImport.close")} placement="left">
          <span>
            <IconButton
              className={classes.closeButton}
              onClick={onClose}
              disabled={importing}
              aria-label={i18n.t("contactImport.close")}
            >
              <CloseIcon />
            </IconButton>
          </span>
        </Tooltip>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{i18n.t("contactImport.description")}</DialogContentText>

        <div className={classes.controls}>
          <FormControl variant="outlined" size="small" className={classes.connectionField}>
            <InputLabel>{i18n.t("contactImport.connection")}</InputLabel>
            <Select
              value={whatsappId}
              onChange={event => {
                setWhatsappId(event.target.value);
                setPreview(null);
                setResult(null);
              }}
              label={i18n.t("contactImport.connection")}
              disabled={loadingWhatsApps || loading || importing}
            >
              {connectedWhatsApps.map(item => (
                <MenuItem key={item.id} value={String(item.id)}>
                  {item.name} {item.phoneNumber ? `· ${item.phoneNumber}` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="primary"
            onClick={loadPreview}
            disabled={!whatsappId || loading || importing}
          >
            {i18n.t("contactImport.loadPreview")}
          </Button>
          {preview && (
            <>
              <TextField
                className={classes.searchField}
                size="small"
                variant="outlined"
                label={i18n.t("contactImport.search")}
                value={search}
                onChange={event => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
              />
              <FormControl variant="outlined" size="small" className={classes.statusFilterField}>
                <InputLabel className={classes.filterLabel}>
                  {i18n.t("contactImport.statusFilter")}
                </InputLabel>
                <Select
                  value={statusFilter}
                  onChange={event => {
                    setStatusFilter(event.target.value);
                    setPage(0);
                  }}
                  label={i18n.t("contactImport.statusFilter")}
                >
                  {["all", "new", "update", "existing", "protected"].map(status => (
                    <MenuItem key={status} value={status}>
                      <Tooltip
                        title={i18n.t(`contactImport.statusHelp.${status}`)}
                        placement="right"
                        enterDelay={300}
                      >
                        <span className={classes.tooltipOption}>
                          {i18n.t(`contactImport.status.${status}`)}
                        </span>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" size="small" className={classes.sourceFilterField}>
                <InputLabel className={classes.filterLabel}>
                  {i18n.t("contactImport.sourceFilter")}
                </InputLabel>
                <Select
                  value={sourceFilter}
                  onChange={event => {
                    setSourceFilter(event.target.value);
                    setPage(0);
                  }}
                  label={i18n.t("contactImport.sourceFilter")}
                >
                  {["all", "addressBook", "named", "verified", "profile", "provisional"].map(source => (
                    <MenuItem key={source} value={source}>
                      <Tooltip
                        title={i18n.t(`contactImport.sourceHelp.${source}`)}
                        placement="right"
                        enterDelay={300}
                      >
                        <span className={classes.tooltipOption}>
                          {i18n.t(`contactImport.source.${source}`)}
                        </span>
                      </Tooltip>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </div>

        {!loadingWhatsApps && connectedWhatsApps.length === 0 && (
          <Typography color="error">{i18n.t("contactImport.noConnections")}</Typography>
        )}

        {loading && (
          <div className={classes.loading}><CircularProgress /></div>
        )}

        {preview && !loading && (
          <>
            <div className={classes.summary}>
              {renderSummaryChip("available", preview.summary.available)}
              {renderSummaryChip("new", preview.summary.new)}
              {renderSummaryChip("update", preview.summary.update)}
              {renderSummaryChip("existing", preview.summary.existing)}
              {renderSummaryChip("protected", preview.summary.protected)}
              {renderSummaryChip("duplicates", preview.summary.duplicatesConsolidated)}
              {renderSummaryChip(
                "skipped",
                preview.summary.groupsSkipped + preview.summary.internalIdsSkipped
              )}
            </div>

            <Button size="small" onClick={selectRecommended} disabled={importing}>
              {i18n.t("contactImport.selectRecommended")}
            </Button>
            <Typography variant="caption" color="textSecondary">
              {i18n.t("contactImport.recommendedHelp")}
            </Typography>
            {selectedKeys.size > 100 && (
              <Typography variant="caption" className={classes.protectedName}>
                {i18n.t("contactImport.bulkPhotoWarning")}
              </Typography>
            )}

            <div className={classes.tableContainer}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allFilteredSelected}
                        indeterminate={!allFilteredSelected && someFilteredSelected}
                        onChange={toggleAllFiltered}
                      />
                    </TableCell>
                    <TableCell>{i18n.t("contactImport.table.name")}</TableCell>
                    <TableCell>{i18n.t("contactImport.table.number")}</TableCell>
                    <TableCell>{i18n.t("contactImport.table.status")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedContacts.map(contact => (
                    <TableRow key={contact.key} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedKeys.has(contact.key)}
                          onChange={() => toggleContact(contact.key)}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{contact.name}</Typography>
                        <Typography variant="caption" className={classes.nameSource}>
                          {i18n.t(`contacts.nameSources.${contact.nameSource}`)}
                          {contact.duplicateCount > 1
                            ? ` · ${i18n.t("contactImport.duplicatesInSource", { count: contact.duplicateCount })}`
                            : ""}
                        </Typography>
                        {contact.status === "protected" && (
                          <Typography variant="caption" className={classes.protectedName}>
                            {i18n.t("contactImport.protectedName", { name: contact.existingName })}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{contact.number}</TableCell>
                      <TableCell>{statusChip(contact.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <TablePagination
              component="div"
              count={filteredContacts.length}
              page={page}
              onChangePage={(event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={event => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={i18n.t("contactImport.rowsPerPage")}
            />
          </>
        )}

        {result && (
          <div className={classes.result}>
            <Typography variant="h6">{i18n.t("contactImport.resultTitle")}</Typography>
            <Typography>
              {i18n.t("contactImport.result", {
                created: result.created,
                updated: result.updated,
                protected: result.protected,
                unchanged: result.unchanged,
                errors: result.errors.length,
                photosSkipped: result.photosSkipped,
              })}
            </Typography>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          className={classes.footerCloseButton}
          variant="outlined"
          onClick={onClose}
          disabled={importing}
        >
          {result ? i18n.t("contactImport.close") : i18n.t("contactImport.cancel")}
        </Button>
        {preview && !result && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleImport}
            disabled={!selectedKeys.size || importing}
          >
            {importing
              ? i18n.t("contactImport.importing")
              : i18n.t("contactImport.importSelected", { count: selectedKeys.size })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportContactsModal;
